import { skipToken, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { isLoyaltyPointsEnabled } from "@/lib/feature-flags"
import { pointsService } from "../services/points-service"
import type { HistoryParams, LeaderboardParams, RedeemInput } from "../types"

export const pointsKeys = {
  all: ["points"] as const,
  balance: () => [...pointsKeys.all, "balance"] as const,
  history: (params?: HistoryParams) => [...pointsKeys.all, "history", params] as const,
  leaderboard: (params?: LeaderboardParams) => [...pointsKeys.all, "leaderboard", params] as const,
  rewards: () => [...pointsKeys.all, "rewards"] as const,
  redemptions: (params?: { status?: string }) => [...pointsKeys.all, "redemptions", params] as const,
}

export function usePointsBalance(userId: string | undefined) {
  const enabled = isLoyaltyPointsEnabled()
  return useQuery({
    queryKey: pointsKeys.balance(),
    queryFn: userId && enabled ? () => pointsService.getBalance() : skipToken,
    enabled: enabled && !!userId,
  })
}

export function usePointsHistory(params?: HistoryParams) {
  return useQuery({
    queryKey: pointsKeys.history(params),
    queryFn: () => pointsService.getHistory(params),
    enabled: isLoyaltyPointsEnabled(),
    placeholderData: (prev) => prev,
  })
}

export function useLeaderboard(params?: LeaderboardParams) {
  return useQuery({
    queryKey: pointsKeys.leaderboard(params),
    queryFn: () => pointsService.getLeaderboard(params),
    enabled: isLoyaltyPointsEnabled(),
  })
}

export function useRewards() {
  return useQuery({
    queryKey: pointsKeys.rewards(),
    queryFn: () => pointsService.getRewards(),
    enabled: isLoyaltyPointsEnabled(),
  })
}

export function useRedeemReward() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: RedeemInput) => pointsService.redeem(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: pointsKeys.balance() })
      queryClient.invalidateQueries({ queryKey: pointsKeys.history() })
      queryClient.invalidateQueries({ queryKey: pointsKeys.rewards() })
      queryClient.invalidateQueries({ queryKey: pointsKeys.redemptions() })
    },
  })
}

export function useMyRedemptions(params?: { status?: string }) {
  return useQuery({
    queryKey: pointsKeys.redemptions(params),
    queryFn: () => pointsService.getMyRedemptions(params),
    enabled: isLoyaltyPointsEnabled(),
  })
}
